import { useState } from "react";
import { Loader2, User, Shield, MessageSquare, Phone, Megaphone, ChevronRight, Activity, Gift } from "lucide-react";
import { TopInviterBadge } from "@/components/TopInviterBadge";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/PasswordInput";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";

const WHATSAPP_URL = "https://wa.me/97336655430";

function Section({ icon: Icon, title, children, testId }) {
  return (
    <div className="bg-white rounded-xl border border-app-border p-5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-4 h-4 text-olive" />
        <h2 className="text-xs font-bold uppercase tracking-widest text-olive">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function ProfileSection() {
  const { user, refresh } = useAuth();
  const { t } = useI18n();
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [email, setEmail] = useState(user?.email || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/auth/me", { name, phone, email });
      toast.success(t("settings.saved"));
      await refresh();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section icon={User} title={t("settings.profile")} testId="settings-profile">
      {user?.top_inviter && (
        <div className="mb-4 flex items-center gap-2" data-testid="profile-badges">
          <TopInviterBadge size="md" />
          <span className="text-xs text-espresso/60">{t("badge.top_inviter_hint")}</span>
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-olive font-bold">{t("account.name")}</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" data-testid="profile-name-input" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-olive font-bold">{t("settings.phone")}</label>
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+973…" className="mt-1" dir="ltr" data-testid="profile-phone-input" />
        </div>
        <div className="sm:col-span-2">
          <label className="text-[10px] uppercase tracking-widest text-olive font-bold">{t("settings.email")}</label>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1" dir="ltr" data-testid="profile-email-input" />
        </div>
      </div>
      <Button onClick={save} disabled={saving} className="mt-4 bg-olive hover:bg-olive/90" data-testid="profile-save-button">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("settings.save")}
      </Button>
    </Section>
  );
}

function SecuritySection() {
  const { t } = useI18n();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const change = async () => {
    if (next !== confirm) return toast.error(t("settings.password_mismatch"));
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success(t("settings.password_changed"));
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Change failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section icon={Shield} title={t("settings.security")} testId="settings-security">
      <div className="grid sm:grid-cols-3 gap-4">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-olive font-bold">{t("settings.current_password")}</label>
          <PasswordInput value={current} onChange={(e) => setCurrent(e.target.value)} className="mt-1" testId="security-current-input" testIdSuffix="security-current" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-olive font-bold">{t("settings.new_password")}</label>
          <PasswordInput value={next} onChange={(e) => setNext(e.target.value)} className="mt-1" autoComplete="new-password" testId="security-new-input" testIdSuffix="security-new" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-olive font-bold">{t("settings.confirm_password")}</label>
          <PasswordInput value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1" autoComplete="new-password" testId="security-confirm-input" testIdSuffix="security-confirm" />
        </div>
      </div>
      <Button
        onClick={change}
        disabled={saving || next.length < 6}
        className="mt-4 bg-olive hover:bg-olive/90"
        data-testid="change-password-button"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("settings.change_password")}
      </Button>
    </Section>
  );
}

function NavButton({ icon: Icon, label, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full bg-white rounded-xl border border-app-border p-5 flex items-center gap-3 hover:border-olive/50 hover:bg-olive/5 transition-colors text-left rtl:text-right"
      data-testid={testId}
    >
      <Icon className="w-4 h-4 text-olive shrink-0" />
      <span className="text-sm font-semibold text-espresso flex-1">{label}</span>
      <ChevronRight className="w-4 h-4 text-espresso/40 rtl:rotate-180" />
    </button>
  );
}

function ContactSection() {
  const { t } = useI18n();
  return (
    <Section icon={Phone} title={t("settings.contact")} testId="settings-contact">
      <a
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1fb757] text-white font-semibold text-sm px-4 py-2.5 rounded-lg transition-colors"
        data-testid="whatsapp-contact-button"
      >
        <svg viewBox="0 0 32 32" className="w-5 h-5 fill-white" aria-hidden="true">
          <path d="M16.004 3.2c-7.06 0-12.8 5.74-12.8 12.8 0 2.26.59 4.46 1.71 6.4L3.2 28.8l6.59-1.68a12.74 12.74 0 0 0 6.21 1.58h.01c7.06 0 12.79-5.74 12.79-12.8s-5.74-12.7-12.8-12.7zm0 23.36h-.01a10.6 10.6 0 0 1-5.4-1.48l-.39-.23-3.91 1 1.04-3.81-.25-.4a10.55 10.55 0 0 1-1.62-5.64c0-5.87 4.78-10.64 10.65-10.64a10.6 10.6 0 0 1 10.64 10.66c0 5.87-4.78 10.64-10.65 10.64zm5.84-7.97c-.32-.16-1.89-.93-2.18-1.04-.29-.11-.5-.16-.72.16-.21.32-.82 1.04-1 1.25-.19.21-.37.24-.69.08-.32-.16-1.35-.5-2.57-1.59-.95-.85-1.59-1.89-1.78-2.21-.19-.32-.02-.49.14-.65.14-.14.32-.37.48-.56.16-.19.21-.32.32-.53.11-.21.05-.4-.03-.56-.08-.16-.72-1.73-.98-2.37-.26-.62-.52-.54-.72-.55l-.61-.01c-.21 0-.56.08-.85.4-.29.32-1.12 1.09-1.12 2.66s1.15 3.08 1.31 3.3c.16.21 2.26 3.45 5.47 4.84.76.33 1.36.53 1.83.67.77.25 1.47.21 2.02.13.62-.09 1.89-.77 2.16-1.52.27-.75.27-1.39.19-1.52-.08-.13-.29-.21-.61-.37z" />
        </svg>
        {t("settings.whatsapp")}
      </a>
    </Section>
  );
}

export default function SettingsPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="settings-page">
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-espresso">
        {t("account.title")}
      </h1>
      <ProfileSection />
      <SecuritySection />
      {user?.role === "admin" && (
        <NavButton
          icon={Megaphone}
          label={t("ann.title")}
          onClick={() => navigate("/announcement")}
          testId="open-announcement-button"
        />
      )}
      {user?.role === "admin" && (
        <NavButton
          icon={Activity}
          label={t("activity.title")}
          onClick={() => navigate("/admin/activity")}
          testId="open-user-activity-button"
        />
      )}
      <NavButton
        icon={Gift}
        label={t("ref.title")}
        onClick={() => navigate("/referrals")}
        testId="open-referrals-button"
      />
      <NavButton
        icon={MessageSquare}
        label={t("settings.feedback")}
        onClick={() => navigate("/feedback")}
        testId="open-feedback-button"
      />
      <ContactSection />
    </div>
  );
}
